import {Injectable, Optional} from '@angular/core';
import {Store} from '@ngrx/store';
import {defer, Observable, of, throwError} from 'rxjs';
import {distinctUntilChanged, map, take, timeout} from 'rxjs/operators';
import {CommandService} from '@core/services/command.service';
import {GraphQLService} from '@core/services/graphql.service';
import {PlatformCommand, PlatformCommandResponse} from '@shared/commands/command-base';
import {EventStatus} from '@shared/models/notification-message.model';
import type {MessengerConversationOrganisationPort} from './conversation-organisation.port';
import {notBuilt} from './messenger-port';
import {ConversationCall, ConversationHit, ConversationScope, MutedUntil} from './messenger.models';
import {conversationCallOf} from './call-log';
import {ConversationMarks, ConversationMarksChangedAction} from '../store/collaboration.action';
import {CollaborationConfig} from '../store/collaboration.config';
import {CollaborationQuery} from '../store/collaboration.query';
import {CollaborationState} from '../store/collaboration.reducer';
import {getConversationListInfo} from '../store/collaboration.selector';

/** One conversation of the conversation list query's answer to a `search`. */
interface ConversationSearchAnswer {
  id: string;
  title: string;
  matchedOn: string;
}

const MATCHED_ON: ReadonlyArray<ConversationHit['matchedOn']> = ['title', 'member', 'message'];

/**
 * The conversation organisation port on the server. B-05..B-08 have shipped (ITP-E-206-02): each person's pin, mute,
 * archive and mark-unread, kept on their own member row; B-01, the search, has shipped (ITP-E-206-05); B-03, the call
 * history from the stored calls, has shipped (ITP-E-208-05); B-02, the unread mentions, has shipped (ITP-E-207-03); the
 * rest has not (ITP-P-33 Release 1).
 *
 * The reads answer from the loaded list's items, which carry the reader's marks (`isPinned`, `mutedUntil`, `isArchived`,
 * `markedUnread`, `hasUnreadMention`), and answer again whenever those marks change. The writes run the four platform commands; the list
 * keeps the marks each one answers.
 */
@Injectable({providedIn: 'root'})
export class MessengerConversationOrganisationReal implements MessengerConversationOrganisationPort {
  /** A platform command does not fail on a lost connection, so a bounded wait stands in for one (as the calls' do). */
  static readonly commandTimeoutMs = 30000;

  /**
   * `graphQLService` reads the search (B-01) and the call history (B-03); the marks need only the store and the commands. It
   * is optional because the port builds this adapter for every method, so a component that never searches can be built
   * without it; the app always
   * has it (CoreModule).
   */
  constructor(private collaborationStore: Store<CollaborationState>,
              private commandService: CommandService,
              @Optional() private graphQLService?: GraphQLService) {
  }

  /**
   * B-01 (ITP-E-206-05): `search` on the conversation list query. The server searches the reader's own conversations by
   * title, member names and the text of the messages they may see, and answers each with what it was found by. A blank
   * text asks nothing and finds nothing.
   */
  search(text: string): Observable<ConversationHit[]> {
    const query = (text || '').trim();
    if (!query) {
      return of([]);
    }
    if (!this.graphQLService) {
      return throwError(() => new Error('Messenger B-01: there is no GraphQL service to search with.'));
    }
    return this.graphQLService.executePostRequest(CollaborationConfig.CollaborationApiKey,
      CollaborationQuery.searchConversations(query)).pipe(
      map(response => {
        const answers: ConversationSearchAnswer[] = response?.data?.chat?.queryConversations;
        if (!Array.isArray(answers)) {
          throw new Error('Messenger B-01: the conversation search was not answered.');
        }
        return answers
          .filter(answer => answer?.id && MATCHED_ON.includes(answer.matchedOn as ConversationHit['matchedOn']))
          .map((answer): ConversationHit => ({
            conversationId: answer.id,
            title: answer.title || '',
            matchedOn: answer.matchedOn as ConversationHit['matchedOn']
          }));
      }));
  }

  /**
   * B-02 (ITP-E-207-03): `hasUnreadMention` on the list item. A message mentioning the reader, by name or as `@everyone`, is
   * still unread for them; reading past it clears it. Drives the Mentions filter and the row's @ mark.
   */
  unreadMentions(conversationIds: string[]): Observable<Record<string, boolean>> {
    return this.marks(conversationIds, item => !!item?.hasUnreadMention);
  }

  /**
   * B-03 (ITP-E-208-05): `conversationCalls(conversationId)`. The server reads the conversation's stored calls the reader
   * took part in, newest first, each with what it was for them; a conversation they are not (or no longer) in answers none.
   * No conversation asks nothing.
   */
  callHistory(conversationId: string): Observable<ConversationCall[]> {
    if (!conversationId) {
      return of([]);
    }
    if (!this.graphQLService) {
      return throwError(() => new Error('Messenger B-03: there is no GraphQL service to read the call history with.'));
    }
    return this.graphQLService.executePostRequest(CollaborationConfig.CollaborationApiKey,
      CollaborationQuery.conversationCalls(conversationId)).pipe(
      map(response => {
        const answers: any[] = response?.data?.conversationCalls;
        if (!Array.isArray(answers)) {
          throw new Error('Messenger B-03: the call history was not answered.');
        }
        return answers.map(conversationCallOf).filter((call): call is ConversationCall => !!call);
      }));
  }

  scopes(conversationIds: string[]): Observable<Record<string, ConversationScope>> {
    return notBuilt('B-04');
  }

  /** B-05: `isPinned` on the list item. */
  pinned(conversationIds: string[]): Observable<Record<string, boolean>> {
    return this.marks(conversationIds, item => !!item?.isPinned);
  }

  /** B-05: `PinConversationCommand`. A fourth pin is refused by the server. */
  setPinned(conversationId: string, pinned: boolean): Observable<void> {
    return this.organise('PinConversationCommand', conversationId, {Pinned: pinned});
  }

  /** B-06: `mutedUntil` on the list item: `always`, or an end time still ahead. A mute that has run out is absent. */
  mutedUntil(conversationIds: string[]): Observable<Record<string, MutedUntil>> {
    return this.listItems(conversationIds).pipe(
      map(items => {
        const muted: Record<string, MutedUntil> = {};
        conversationIds.forEach(id => {
          const until = items[id]?.mutedUntil;
          if (until === 'always' || (typeof until === 'string' && new Date(until).getTime() > Date.now())) {
            muted[id] = until;
          }
        });
        return muted;
      }),
      distinctUntilChanged(sameRecord));
  }

  /** B-06: `MuteConversationCommand(until)`: an ISO end time or `always`; null unmutes. */
  mute(conversationId: string, until: MutedUntil | null): Observable<void> {
    return this.organise('MuteConversationCommand', conversationId, {Until: until ?? null});
  }

  /** B-07: `isArchived` on the list item. The list asks for the archived conversations after its own (`includeArchived`). */
  archived(conversationIds: string[]): Observable<Record<string, boolean>> {
    return this.marks(conversationIds, item => !!item?.isArchived);
  }

  /** B-07: `ArchiveConversationCommand`. */
  setArchived(conversationId: string, archived: boolean): Observable<void> {
    return this.organise('ArchiveConversationCommand', conversationId, {Archived: archived});
  }

  /** B-08: `markedUnread` on the list item. Reading the conversation clears it. */
  markedUnread(conversationIds: string[]): Observable<Record<string, boolean>> {
    return this.marks(conversationIds, item => !!item?.markedUnread);
  }

  /** B-08: `MarkConversationUnreadCommand`. */
  setMarkedUnread(conversationId: string, unread: boolean): Observable<void> {
    return this.organise('MarkConversationUnreadCommand', conversationId, {Unread: unread});
  }

  /** The loaded list's items among `conversationIds`, by id, answering again whenever the list changes. */
  private listItems(conversationIds: string[]): Observable<Record<string, any>> {
    return this.collaborationStore.select(getConversationListInfo).pipe(map(info => {
      const wanted = new Set(conversationIds);
      const items: Record<string, any> = {};
      (info?.queryConversations || []).filter(item => item && wanted.has(item.id)).forEach(item => items[item.id] = item);
      return items;
    }));
  }

  /** One true/false mark for each of `conversationIds`, answered again only when one of them changes. */
  private marks(conversationIds: string[], mark: (item: any) => boolean): Observable<Record<string, boolean>> {
    return this.listItems(conversationIds).pipe(
      map(items => {
        const marks: Record<string, boolean> = {};
        conversationIds.forEach(id => marks[id] = mark(items[id]));
        return marks;
      }),
      distinctUntilChanged(sameRecord));
  }

  /**
   * Runs one of the four commands for the reader's own row of `conversationId`, when subscribed. Its answer is the
   * conversation's marks as the server now holds them: the list keeps them, so every read answers again. A refusal (a
   * fourth pin, a conversation the reader is not in) or no answer fails the write, and the marks stay as they were.
   */
  private organise(name: string, conversationId: string, values: Record<string, boolean | string | null>): Observable<void> {
    return defer(() => {
      const command = new PlatformCommand(name, 'CollaborationApi');
      command.fieldValues = {ConversationId: conversationId, ...values};
      command.waitForResponse = true;
      return this.commandService.executePlatformCommand(command);
    }).pipe(
      take(1),
      timeout(MessengerConversationOrganisationReal.commandTimeoutMs),
      map((response: PlatformCommandResponse) => {
        if (!response || response.status === EventStatus.Failed || !response.fieldValues) {
          throw new Error(`${name} was not done for conversation ${conversationId}.`);
        }
        this.collaborationStore.dispatch(new ConversationMarksChangedAction(conversationId, marksOf(response.fieldValues)));
      }));
  }
}

/** The marks a command answered (its field values are named as the server sets them). */
function marksOf(answer: Record<string, any>): ConversationMarks {
  return {
    isPinned: !!answer['IsPinned'],
    mutedUntil: answer['MutedUntil'] || null,
    isArchived: !!answer['IsArchived'],
    markedUnread: !!answer['MarkedUnread']
  };
}

/** Two answers hold the same marks for the same conversations. */
function sameRecord<T>(a: Record<string, T>, b: Record<string, T>): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every(key => key in b && a[key] === b[key]);
}
